import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { buildLoginUrl } from '../utils/authRedirect';
import './checkout.css';
import {track,confirmPurchase} from '../analytics/service';

function cartSignature(items) {
  return JSON.stringify(items.map(item => [
    Number(item.id_fornitore), Number(item.id_servizio),
    Number(item.cross_sell_item_id || 0), Number(item.qty),
  ]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
}

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sessionId = searchParams.get('session_id');
  const { clearCart, items } = useCart();
  const cartItems = useRef(items);
  cartItems.current = items;
  const [result, setResult] = useState({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const cleared = useRef(new Set());
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  useEffect(()=>{
    // PayPal is measured by its capture return page; wallet has no external return.
    if (sessionId) track('checkout_returned',{screen:'checkout_return',gateway:'stripe'},`return:${location.key}`);
  },[location.key,sessionId]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setResult({ status: 'loading' });
    const fetchSummary = async () => {
      if (!sessionId) {
        // Wallet/PayPal clear the cart in their own verified payment flows.
        // Their URL flags alone cannot certify an outcome here.
        setResult({ status: 'unverified' });
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) {
        setResult({ status: 'unauthenticated' });
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/api/checkout/session/${encodeURIComponent(sessionId)}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!active) return;
        if (localStorage.getItem('token') !== token) {
          setResult({ status: 'unauthenticated' });
          return;
        }
        if (res.status === 401) {
          setResult({ status: 'unauthenticated' });
          return;
        }
        const data = await res.json();
        if (!active) return;
        if (localStorage.getItem('token') !== token) {
          setResult({ status: 'unauthenticated' });
          return;
        }
        // authMiddleware also uses 403 for an invalid JWT when mounted directly.
        if (res.status === 403 && data.error === 'Token non valido') {
          setResult({ status: 'unauthenticated' });
          return;
        }
        if (!res.ok) throw new Error('summary_unavailable');
        if (!['confirmed', 'processing', 'not_completed'].includes(data.status)
          || (data.status === 'confirmed' && !data.orders?.length)) {
          throw new Error('invalid_summary');
        }
        if (data.status === 'confirmed') {
          void confirmPurchase(sessionId);
          // Refreshing this receipt must not erase a later cart in this tab.
          const purchasedItems = data.orders.flatMap(order => (order.items || []).map(item => ({
            id_fornitore: order.id_fornitore, id_servizio: item.service_id,
            cross_sell_item_id: item.cross_sell_item_id, qty: item.quantity,
          })));
          const matchesCart = purchasedItems.length > 0
            && cartSignature(purchasedItems) === cartSignature(cartItems.current || []);
          const key = `stirup_checkout_cleared:${sessionId}`;
          let alreadyCleared = cleared.current.has(sessionId);
          try { alreadyCleared ||= sessionStorage.getItem(key) === '1'; } catch {}
          if (!alreadyCleared && matchesCart) {
            clearCart();
            cleared.current.add(sessionId);
            try { sessionStorage.setItem(key, '1'); } catch {}
          }
        }
        setResult(data);
      } catch (err) {
        if (active && err.name !== 'AbortError') setResult({ status: 'error' });
      }
    };
    fetchSummary();
    return () => { active = false; controller.abort(); };
  }, [sessionId, attempt, clearCart, API_BASE]);

  const titles = {
    loading: 'Verifica pagamento',
    confirmed: 'Pagamento confermato',
    processing: 'Pagamento in elaborazione',
    not_completed: 'Pagamento non completato',
    unauthenticated: 'Accedi per verificare il pagamento',
    error: 'Impossibile verificare il pagamento',
    unverified: 'Consulta i tuoi ordini',
  };

  return (
    <div className="checkout-page">
      <div className="checkout-card">
        <div className={`checkout-icon ${result.status === 'confirmed' ? 'success' : 'cancel'}`}>
          {result.status === 'confirmed' ? '✅' : 'ℹ️'}
        </div>
        <h1>{titles[result.status]}</h1>
        <div aria-live="polite">
          {result.status === 'loading' && <p>Sto preparando il riepilogo…</p>}
          {result.status === 'processing' && <p>La conferma dell’ordine non è ancora disponibile. Ricontrolla tra poco senza ripetere il pagamento.</p>}
          {result.status === 'not_completed' && <p>{result.expired ? 'La sessione di pagamento è scaduta.' : 'Il pagamento non risulta completato.'} Il carrello è stato conservato.</p>}
          {result.status === 'unauthenticated' && <p>Accedi con l’account utilizzato per l’acquisto per visualizzare il riepilogo.</p>}
          {result.status === 'error' && <p className="error">Non è stato possibile recuperare il riepilogo. Non ripetere il pagamento senza averne verificato l’esito.</p>}
          {result.status === 'unverified' && <p>Puoi verificare l’esito dell’acquisto nella sezione “I miei Ordini”.</p>}
          {result.status === 'confirmed' && <div className="summary">
            {result.orders.map(ord => <div key={ord.id} className="summary-block">
              <h3>{ord.fornitore_nome || `Prenotazione #${ord.id}`}</h3>
              <p>Prenotazione #{ord.id}</p>
              <p>Ritiro: <strong>{ord.data_ritiro}</strong><br />Consegna: <strong>{ord.data_consegna}</strong></p>
              <div className="summary-total">Totale: <strong>{(Number(ord.totale_cents) / 100).toFixed(2)} {result.currency}</strong></div>
            </div>)}
          </div>}
        </div>
        <div className="actions">
          {result.status === 'unauthenticated' && <Link to={buildLoginUrl(location)} className="btn primary">Accedi</Link>}
          {['processing', 'error'].includes(result.status) && <button type="button" className="btn" onClick={() => setAttempt(n => n + 1)}>Ricontrolla</button>}
          <Link to="/ordini" className="btn primary">I miei Ordini</Link>
          <Link to="/" className="btn">Torna alla Home</Link>
        </div>
      </div>
    </div>
  );
}
